import {
  Dialog,
  DialogPanel,
  Transition,
  TransitionChild,
} from "@headlessui/react";
import { Fragment, ReactNode } from "react";
import { useMatch } from "react-router";
import { useSearchParams } from "react-router";
import { twMerge } from "tailwind-merge";
import {
  DISCOVER,
  DISCOVER_RECENTLY_ADDED,
  DISCOVER_SEARCH,
  DISCOVER_SUPPLIER_MAP,
  LOGIN,
  ORDER_BUYER,
  ORDER_SUCCESS_BUYER,
  PAYMENT_LINK,
  SIGNUP,
  UNIFIED_CHECKOUT_BASKET,
} from "../../constants/routes";
import { useSellarUser } from "../../hooks";
import { useMenuDrawerContext } from "./MenuDrawerContextProvider.shared";
import { zLayer, FloatingLayerProvider } from "@sellar-io/components";

/** UIKit-like decelerating ease (iOS sheets / side menus) */
const IOS_MOTION = "duration-[350ms] ease-[cubic-bezier(0.32,0.72,0,1)]";

const useShouldShowMenu = () => {
  const { user, loading } = useSellarUser();
  const [searchParams] = useSearchParams();
  const storefrontLink =
    searchParams.get("storefrontLink") || searchParams.get("priceTierLink");
  const isSignupScreen = !!useMatch(SIGNUP);
  const isSigninScreen = !!useMatch(LOGIN);
  const isDiscover = !!useMatch(DISCOVER);
  const isDiscoverSearch = !!useMatch(DISCOVER_SEARCH);
  const isDiscoverRecentlyAdded = !!useMatch(DISCOVER_RECENTLY_ADDED);
  const isDiscoverSupplierMap = !!useMatch(DISCOVER_SUPPLIER_MAP);
  const isStorefrontScreenLoggedOut = !!useMatch(ORDER_BUYER.pattern) && !user;
  const isCheckoutSuccessScreen = !!useMatch(ORDER_SUCCESS_BUYER.pattern);
  const isPaymentLinkScreen = !!useMatch(PAYMENT_LINK.pattern);
  const isUnifiedCheckoutScreen = !!useMatch({
    path: UNIFIED_CHECKOUT_BASKET.pattern,
    end: false,
  });

  const isDiscoverScreen =
    isDiscover ||
    isDiscoverSearch ||
    isDiscoverRecentlyAdded ||
    isDiscoverSupplierMap;
  const isLoggedOutDiscoverScreen = isDiscoverScreen && !user;

  return (
    !loading &&
    !isLoggedOutDiscoverScreen &&
    !storefrontLink &&
    !isSignupScreen &&
    !isSigninScreen &&
    !isStorefrontScreenLoggedOut &&
    !isPaymentLinkScreen &&
    !isUnifiedCheckoutScreen &&
    !isCheckoutSuccessScreen
  );
};

const MobileMenuOverlay = ({
  open,
  onClose,
  children,
}: {
  open: boolean;
  onClose: () => void;
  children: ReactNode;
}) => (
  <Transition show={open} as={Fragment}>
    <Dialog onClose={onClose} className={`relative ${zLayer.drawer}`}>
      <TransitionChild
        as={Fragment}
        enter={IOS_MOTION}
        enterFrom="opacity-0"
        enterTo="opacity-100"
        leave={IOS_MOTION}
        leaveFrom="opacity-100"
        leaveTo="opacity-0"
      >
        <div className="fixed inset-0 bg-black/40" aria-hidden="true" />
      </TransitionChild>
      <TransitionChild
        as={Fragment}
        enter={IOS_MOTION}
        enterFrom="-translate-x-full"
        enterTo="translate-x-0"
        leave={IOS_MOTION}
        leaveFrom="translate-x-0"
        leaveTo="-translate-x-full"
      >
        <DialogPanel
          className={twMerge(
            "fixed inset-y-0 left-0 flex h-dvh w-[250px] max-w-[85vw] flex-col",
            "overflow-y-auto overscroll-contain bg-white",
            "border-r border-solid border-black/5",
            "shadow-[8px_0_32px_rgba(0,0,0,0.12)]",
            "transform-gpu",
          )}
        >
          <FloatingLayerProvider host="drawer">
            {children}
          </FloatingLayerProvider>
        </DialogPanel>
      </TransitionChild>
    </Dialog>
  </Transition>
);

const DesktopMenuSidebar = ({
  rail,
  onScreen,
  children,
}: {
  rail: boolean;
  onScreen: boolean;
  children: ReactNode;
}) => (
  <div
    className={twMerge(
      // Fill the shell's remaining height under the PWA banner (parent is flex/grid constrained).
      // Avoid h-dvh here — it ignores the banner spacer and pushes the sidebar off-screen.
      "sticky top-0 z-10 flex h-full min-h-0 flex-col self-stretch overflow-y-auto overscroll-contain border-r border-solid bg-white print:hidden",
      "transform-gpu transition-[width,transform] duration-[350ms] ease-[cubic-bezier(0.32,0.72,0,1)]",
      rail ? "w-[60px] min-w-[60px]" : "w-[250px]",
      onScreen ? "translate-x-0" : "-translate-x-full",
    )}
  >
    {children}
  </div>
);

export const MenuDrawer = ({ children }: { children: ReactNode }) => {
  const {
    isRailDisplayType,
    isMobileDisplayType: isOverlayDisplayType,
    isOnScreen,
    setIsOnScreen,
  } = useMenuDrawerContext();
  const shouldShowMenu = useShouldShowMenu();

  if (isOverlayDisplayType) {
    return (
      <MobileMenuOverlay
        open={shouldShowMenu && isOnScreen}
        onClose={() => setIsOnScreen(false)}
      >
        {children}
      </MobileMenuOverlay>
    );
  }

  return shouldShowMenu ? (
    <DesktopMenuSidebar rail={isRailDisplayType} onScreen={isOnScreen}>
      {children}
    </DesktopMenuSidebar>
  ) : (
    <div aria-hidden="true" />
  );
};
